import { useCallback, useEffect, useRef, useState, useTransition } from 'react';

import { useSearchParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';

export type ExecutedFilter = 'all' | 'yes' | 'no';
export type MultipleWaiversFilter = 'all' | 'yes' | 'no';
export type QuickDatePreset = 'none' | 'recent' | 'updated' | 'open' | 'closed';
export type LogicOperator = 'AND' | 'OR';

export interface FilterState {
  searchText: string;
  clientNumber: string;
  clientName: string;
  adverseClient: string;
  matterNumber: string;
  adverseAttorney: string;
  openDateFrom: string;
  openDateTo: string;
  closedDateFrom: string;
  closedDateTo: string;
  page: number;
  showAdvanced: boolean;
  logicOperator: LogicOperator;
  executedFilter: ExecutedFilter;
  multipleWaiversFilter: MultipleWaiversFilter;
  quickDatePreset: QuickDatePreset;
}

export interface FilterActions {
  setSearchText: (value: string) => void;
  setClientNumber: (value: string) => void;
  setClientName: (value: string) => void;
  setAdverseClient: (value: string) => void;
  setMatterNumber: (value: string) => void;
  setAdverseAttorney: (value: string) => void;
  setOpenDateRange: (from: string, to: string) => void;
  setClosedDateRange: (from: string, to: string) => void;
  setPage: (value: number) => void;
  setShowAdvanced: (value: boolean) => void;
  setLogicOperator: (value: LogicOperator) => void;
  setExecutedFilter: (value: ExecutedFilter) => void;
  setMultipleWaiversFilter: (value: MultipleWaiversFilter) => void;
  setQuickDatePreset: (value: QuickDatePreset) => void;
  clearAll: () => void;
}

const DEBOUNCE_MS = 200;

export const useFilterState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Writing filters to the URL re-runs an expensive client-side filter over the
  // full (~140k-row) dataset. Marking those writes as a transition keeps the
  // render interruptible so urgent input updates aren't blocked mid-keystroke.
  const [, startTransition] = useTransition();

  // Local state for text inputs so the UI stays responsive while debouncing
  const [localSearchText, setLocalSearchText] = useState(searchParams.get('q') || '');
  const [localClientNumber, setLocalClientNumber] = useState(searchParams.get('clientNumber') || '');
  const [localClientName, setLocalClientName] = useState(searchParams.get('clientName') || '');
  const [localAdverseClient, setLocalAdverseClient] = useState(searchParams.get('adverseClient') || '');
  const [localMatterNumber, setLocalMatterNumber] = useState(searchParams.get('matterNumber') || '');
  const [localAdverseAttorney, setLocalAdverseAttorney] = useState(searchParams.get('adverseAttorney') || '');

  // Records the last value WE pushed to each text param via the debounced
  // writers. Because those writes lag behind local state, the resulting URL
  // change would otherwise trip the sync effect below and revert freshly typed
  // characters. Tracking our own writes lets us tell an echo of our own update
  // apart from a genuine external navigation (back/forward, shared link).
  const pushedValuesRef = useRef({
    q: searchParams.get('q') || '',
    clientNumber: searchParams.get('clientNumber') || '',
    clientName: searchParams.get('clientName') || '',
    adverseClient: searchParams.get('adverseClient') || '',
    matterNumber: searchParams.get('matterNumber') || '',
    adverseAttorney: searchParams.get('adverseAttorney') || '',
  });

  // Keep local state in sync when URL params change externally (e.g. back/forward nav)
  const prevParamsRef = useRef(searchParams);
  useEffect(() => {
    const prev = prevParamsRef.current;
    if (prev === searchParams) return;
    prevParamsRef.current = searchParams;
    const pushed = pushedValuesRef.current;
    // Only adopt a URL value when it differs from what we last wrote — i.e. it
    // came from external navigation, not our own lagging debounced write —
    // otherwise the effect would revert characters typed during the debounce.
    const textFields = [
      { key: 'q', pushedKey: 'q', local: localSearchText, setLocal: setLocalSearchText },
      { key: 'clientNumber', pushedKey: 'clientNumber', local: localClientNumber, setLocal: setLocalClientNumber },
      { key: 'clientName', pushedKey: 'clientName', local: localClientName, setLocal: setLocalClientName },
      { key: 'adverseClient', pushedKey: 'adverseClient', local: localAdverseClient, setLocal: setLocalAdverseClient },
      { key: 'matterNumber', pushedKey: 'matterNumber', local: localMatterNumber, setLocal: setLocalMatterNumber },
      {
        key: 'adverseAttorney',
        pushedKey: 'adverseAttorney',
        local: localAdverseAttorney,
        setLocal: setLocalAdverseAttorney,
      },
    ] as const;
    for (const field of textFields) {
      const urlValue = searchParams.get(field.key) || '';
      if (urlValue !== pushed[field.pushedKey]) {
        pushed[field.pushedKey] = urlValue;
        if (urlValue !== field.local) field.setLocal(urlValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setParam = useCallback(
    (key: string, value: string, defaultValue = '') => {
      startTransition(() => {
        setSearchParams((prev) => {
          if (value && value !== defaultValue) {
            prev.set(key, value);
          } else {
            prev.delete(key);
          }
          return prev;
        });
      });
    },
    [setSearchParams],
  );

  // Debounced URL param updaters for text fields. Each records the value it is
  // about to write so the sync effect can recognise the resulting URL change as
  // our own echo and avoid reverting newer local input.
  const debouncedSetSearchText = useDebouncedCallback((v: string) => {
    pushedValuesRef.current.q = v;
    setParam('q', v);
  }, DEBOUNCE_MS);
  const debouncedSetClientNumber = useDebouncedCallback((v: string) => {
    pushedValuesRef.current.clientNumber = v;
    setParam('clientNumber', v);
  }, DEBOUNCE_MS);
  const debouncedSetClientName = useDebouncedCallback((v: string) => {
    pushedValuesRef.current.clientName = v;
    setParam('clientName', v);
  }, DEBOUNCE_MS);
  const debouncedSetAdverseClient = useDebouncedCallback((v: string) => {
    pushedValuesRef.current.adverseClient = v;
    setParam('adverseClient', v);
  }, DEBOUNCE_MS);
  const debouncedSetMatterNumber = useDebouncedCallback((v: string) => {
    pushedValuesRef.current.matterNumber = v;
    setParam('matterNumber', v);
  }, DEBOUNCE_MS);
  const debouncedSetAdverseAttorney = useDebouncedCallback((v: string) => {
    pushedValuesRef.current.adverseAttorney = v;
    setParam('adverseAttorney', v);
  }, DEBOUNCE_MS);

  // The advanced panel state lives in the URL so a shared/bookmarked link that
  // carries advanced filters always reveals them. When the user hasn't toggled
  // it explicitly, auto-expand whenever any advanced filter param is present.
  const hasAdvancedParams =
    Boolean(searchParams.get('clientNumber')) ||
    Boolean(searchParams.get('clientName')) ||
    Boolean(searchParams.get('adverseClient')) ||
    Boolean(searchParams.get('matterNumber')) ||
    Boolean(searchParams.get('adverseAttorney')) ||
    Boolean(searchParams.get('openDateFrom')) ||
    Boolean(searchParams.get('openDateTo')) ||
    Boolean(searchParams.get('closedDateFrom')) ||
    Boolean(searchParams.get('closedDateTo')) ||
    (Boolean(searchParams.get('logicOperator')) && searchParams.get('logicOperator') !== 'AND');
  const advancedParam = searchParams.get('advanced');
  const showAdvanced = advancedParam !== null ? advancedParam === '1' : hasAdvancedParams;

  const state: FilterState = {
    searchText: localSearchText,
    clientNumber: localClientNumber,
    clientName: localClientName,
    adverseClient: localAdverseClient,
    matterNumber: localMatterNumber,
    adverseAttorney: localAdverseAttorney,
    openDateFrom: searchParams.get('openDateFrom') || '',
    openDateTo: searchParams.get('openDateTo') || '',
    closedDateFrom: searchParams.get('closedDateFrom') || '',
    closedDateTo: searchParams.get('closedDateTo') || '',
    page: Number(searchParams.get('page')) || 0,
    showAdvanced,
    logicOperator: (searchParams.get('logicOperator') as LogicOperator) || 'AND',
    executedFilter: (searchParams.get('executedFilter') as ExecutedFilter) || 'all',
    multipleWaiversFilter: (searchParams.get('multipleWaiversFilter') as MultipleWaiversFilter) || 'all',
    quickDatePreset: (searchParams.get('quickDatePreset') as QuickDatePreset) || 'none',
  };

  const actions: FilterActions = {
    setSearchText: (v) => {
      setLocalSearchText(v);
      debouncedSetSearchText(v);
    },
    setClientNumber: (v) => {
      setLocalClientNumber(v);
      debouncedSetClientNumber(v);
    },
    setClientName: (v) => {
      setLocalClientName(v);
      debouncedSetClientName(v);
    },
    setAdverseClient: (v) => {
      setLocalAdverseClient(v);
      debouncedSetAdverseClient(v);
    },
    setMatterNumber: (v) => {
      setLocalMatterNumber(v);
      debouncedSetMatterNumber(v);
    },
    setAdverseAttorney: (v) => {
      setLocalAdverseAttorney(v);
      debouncedSetAdverseAttorney(v);
    },
    // Update both range bounds in a single navigation. Calling the individual
    // setters separately issues two synchronous setSearchParams updates where
    // the second clobbers the first, which would drop the start date.
    setOpenDateRange: (from, to) =>
      startTransition(() =>
        setSearchParams((prev) => {
          if (from) prev.set('openDateFrom', from);
          else prev.delete('openDateFrom');
          if (to) prev.set('openDateTo', to);
          else prev.delete('openDateTo');
          return prev;
        }),
      ),
    setClosedDateRange: (from, to) =>
      startTransition(() =>
        setSearchParams((prev) => {
          if (from) prev.set('closedDateFrom', from);
          else prev.delete('closedDateFrom');
          if (to) prev.set('closedDateTo', to);
          else prev.delete('closedDateTo');
          return prev;
        }),
      ),
    setPage: (v) => setParam('page', String(v), '0'),
    setShowAdvanced: (v) => setParam('advanced', v ? '1' : '0'),
    setLogicOperator: (v) => setParam('logicOperator', v, 'AND'),
    setExecutedFilter: (v) => setParam('executedFilter', v, 'all'),
    setMultipleWaiversFilter: (v) => setParam('multipleWaiversFilter', v, 'all'),
    setQuickDatePreset: (v) => setParam('quickDatePreset', v, 'none'),
    clearAll: () => {
      setLocalSearchText('');
      setLocalClientNumber('');
      setLocalClientName('');
      setLocalAdverseClient('');
      setLocalMatterNumber('');
      setLocalAdverseAttorney('');
      pushedValuesRef.current = {
        q: '',
        clientNumber: '',
        clientName: '',
        adverseClient: '',
        matterNumber: '',
        adverseAttorney: '',
      };
      // Preserve the active tab (letterType) when clearing filters.
      const preserved = new URLSearchParams();
      const currentLetterType = searchParams.get('letterType');
      if (currentLetterType) preserved.set('letterType', currentLetterType);
      startTransition(() => setSearchParams(preserved));
    },
  };

  return { state, actions };
};
